import { css } from "@compiled/react";
import { FormattedMessage, useIntl } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";

import { ContentBlock } from "@/components/wpl/content-block/src";
import { DEFAULT_VALUES } from "@/libraries/web-framework";
import { Row } from "@/libraries/wpl/foundations/src";

const integrationStyles = css({
    "alignItems": "center",
    "display": "flex",
    "justifyContent": "center",
    "padding": "192px 0",
    "backgroundColor": "#ffffff",

    [media.below.xs]: {
        backgroundImage: `url(https://dam-cdn.atl.orangelogic.com/AssetLink/3l7t4k587g51h0d62su00b5402vidhke.svg), 
            url(https://dam-cdn.atl.orangelogic.com/AssetLink/6jnys3iq6vrin1ntsa38p2tf5nqoi2hj.svg)`,
        backgroundPosition: "center top, center bottom",
        backgroundRepeat: "no-repeat, no-repeat",
        backgroundSize: "70%, 65%",
        flexDirection: "column",
        padding: "142px 0"
    },
    [media.only.xs]: {
        backgroundImage: `url(https://dam-cdn.atl.orangelogic.com/AssetLink/3l7t4k587g51h0d62su00b5402vidhke.svg), 
            url(https://dam-cdn.atl.orangelogic.com/AssetLink/6jnys3iq6vrin1ntsa38p2tf5nqoi2hj.svg)`,
        backgroundPosition: "center top, center bottom",
        backgroundRepeat: "no-repeat, no-repeat",
        backgroundSize: "350px, 350px",
        flexDirection: "column"
    },
    [media.only.sm]: {
        backgroundImage: `url(https://dam-cdn.atl.orangelogic.com/AssetLink/v858l68fr8q0jfwbx58by16f55a50na4.svg), 
            url(https://dam-cdn.atl.orangelogic.com/AssetLink/n207d5wnkfysgv5f44ka7t5dmmt4bmw1.svg)`,
        backgroundPosition: "left top 40px, right top 20px",
        backgroundRepeat: "no-repeat, no-repeat",
        backgroundSize: "27% auto, 27% auto",
        padding: "96px 0"
    },
    [media.only.md]: {
        backgroundImage: `url(https://dam-cdn.atl.orangelogic.com/AssetLink/j1yplxamfkhp8x57akpc072jwy2npgfj.svg), 
            url(https://dam-cdn.atl.orangelogic.com/AssetLink/i08302y20562686qs5846raot8he6j04.svg)`,
        backgroundPosition: "left top 40px, right top 50px",
        backgroundRepeat: "no-repeat, no-repeat",
        backgroundSize: "27% auto, 27% auto",
        padding: "96px 0"
    },
    [media.above.lg]: {
        backgroundImage: `url(https://dam-cdn.atl.orangelogic.com/AssetLink/j1yplxamfkhp8x57akpc072jwy2npgfj.svg),
            url(https://dam-cdn.atl.orangelogic.com/AssetLink/i08302y20562686qs5846raot8he6j04.svg)`,
        backgroundPosition: "left center, right top 30px",
        backgroundRepeat: "no-repeat, no-repeat",
        backgroundSize: "auto, auto"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".integrations-content": {
        maxWidth: "720px",
        padding: "16px 0",
        width: "50%",
        margin: "0 auto",

        [media.above.xs]: {
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            h2: {
                fontSize: "2.25rem"
            },
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            p: {
                fontSize: "1rem"
            }
        },
        [media.above.sm]: {
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            h2: {
                marginBlockEnd: "24px"
            }
        },

        [media.below.sm]: {
            maxWidth: "1440px",
            width: "100%",
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            h2: {
                "fontSize": "1.5rem",
                "lineHeight": "1.33333333",
                "webkit-font-smoothing": "antialiased",
                "moz-osx-font-smoothing": "grayscale",
                "color": "#253858",
                "marginBlockEnd": "14px",
                "fontWeight": "500"
            }
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        p: {
            "color": "#091e42",
            "marginBlockEnd": "24px",
            "fontWeight": "normal",
            "webkit-font-smoothing": "antialiased",
            "moz-osx-font-smoothing": "grayscale"
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        a: {
            "margin": "0px",
            "padding": "4px 10px",
            "fontWeight": "500",
            "height": "unset",
            "webkit-font-smoothing": "antialiased",
            "moz-osx-font-smoothing": "grayscale"
        }
    }
});

const IntegrationSection = () => {
    const intl = useIntl();

    return (
        <section css={integrationStyles}>
            <Row>
                <div className="integrations-content">
                    <ContentBlock
                        headerText={intl.formatMessage({
                            id: "JiraSoftwarePageTemplate.integrationSection.header",
                            defaultMessage: "We believe in open relationships"
                        })}
                        bodyText={
                            <FormattedMessage
                                id="JiraSoftwarePageTemplate.integrationSection.body"
                                defaultMessage="If your team uses it, we integrate with it. Easily add your tools from the Atlassian Marketplace, keeping Jira as your central source of truth."
                                values={DEFAULT_VALUES}
                            />
                        }
                        headerType="h2"
                        isCentered
                        orientation="vertical"
                        ctaProps={{
                            buttonProps: {
                                text: intl.formatMessage({
                                    id: "JiraSoftwarePageTemplate.integrationSection.cta",
                                    defaultMessage: "Explore integrations"
                                }),
                                url: "https://marketplace.atlassian.com/",
                                type: "secondary",
                                size: "small",
                                dataEventProps: {
                                    "data-event-component": "linkButton",
                                    "data-event-container":
                                        "integrationSection",
                                    "data-event": "clicked",
                                    "data-product-key": "jiraSoftware"
                                }
                            }
                        }}
                    />
                </div>
            </Row>
        </section>
    );
};

IntegrationSection.displayName = "IntegrationSection";

export default IntegrationSection;
